/** Optional authoring context travels with a generated GLB. It is untrusted
 * inspection data, never an executable import hook or geometry certificate. */
import type { MeshBuildStats } from './mesh-materializer';
export interface GeometrySource { code: string; description: string; stats: MeshBuildStats; embedded?: boolean; }
const KEY = 'traceSandboxBuild';
const MAX_JSON = 2_000_000;
const record = (x: unknown): x is Record<string, unknown> => Boolean(x && typeof x === 'object' && !Array.isArray(x));
const vector = (x: unknown): x is [number, number, number] => Array.isArray(x) && x.length === 3 && x.every(v => typeof v === 'number' && Number.isFinite(v));
async function jsonChunk(blob: Blob) {
    if (blob.size < 20) return null;
    const header = new DataView(await blob.slice(0,20).arrayBuffer());
    const length = header.getUint32(12,true);
    if (header.getUint32(0,true)!==0x46546c67 || header.getUint32(4,true)!==2 || header.getUint32(8,true)!==blob.size
        || header.getUint32(16,true)!==0x4e4f534a || length>MAX_JSON || length%4 || 20+length>blob.size) return null;
    try { const json: unknown=JSON.parse(await blob.slice(20,20+length).text()); return record(json) ? {json,length} : null; } catch { return null; }
}
export async function embedMeshSource(blob: Blob, source: GeometrySource): Promise<Blob> {
    if(source.code.length>120_000 || source.description.length>16_000) return blob;
    const chunk=await jsonChunk(blob); if(!chunk) return blob;
    const asset=record(chunk.json.asset)?chunk.json.asset:{};
    const extras=record(asset.extras)?asset.extras:{};
    const {triangles,meshes,materialSlots,bounds}=source.stats;
    chunk.json.asset={...asset,extras:{...extras,[KEY]:{version:1,code:source.code,description:source.description,stats:{triangles,meshes,materialSlots,bounds}}}};
    const encoded=new TextEncoder().encode(JSON.stringify(chunk.json));
    const padded=new Uint8Array(Math.ceil(encoded.length/4)*4);padded.fill(32);padded.set(encoded);
    if(padded.length>MAX_JSON) return blob;
    const header=new ArrayBuffer(20),view=new DataView(header);
    view.setUint32(0,0x46546c67,true);view.setUint32(4,2,true);view.setUint32(8,blob.size-chunk.length+padded.length,true);
    view.setUint32(12,padded.length,true);view.setUint32(16,0x4e4f534a,true);
    return new Blob([header,padded,blob.slice(20+chunk.length)],{type:blob.type||'model/gltf-binary'});
}
export async function readMeshSource(blob: Blob): Promise<GeometrySource|null> {
    const chunk=await jsonChunk(blob);if(!chunk)return null;
    const asset=chunk.json.asset;if(!record(asset)||!record(asset.extras))return null;
    const data=asset.extras[KEY];if(!record(data)||data.version!==1 || typeof data.code!=='string' || data.code.length>120_000
        || typeof data.description!=='string' || data.description.length>16_000 || !record(data.stats))return null;
    const s=data.stats,b=s.bounds;
    if(typeof s.triangles!=='number'||!Number.isSafeInteger(s.triangles)||s.triangles<0||s.triangles>200_000
        ||typeof s.meshes!=='number'||!Number.isSafeInteger(s.meshes)||s.meshes<1||s.meshes>4000
        ||!Array.isArray(s.materialSlots)||s.materialSlots.length>4000||!s.materialSlots.every(x=>typeof x==='string'&&x.length<=4000)
        ||!record(b)||!vector(b.min)||!vector(b.max)||!vector(b.size))return null;
    const min=b.min,max=b.max,size=b.size;
    if(min.some((v,i)=>v>max[i]||size[i]<0))return null;
    return {code:data.code,description:data.description,embedded:true,stats:{triangles:s.triangles,meshes:s.meshes,materialSlots:s.materialSlots as string[],bounds:{min,max,size}}};
}
